import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { getLegalUI } from "@/i18n/legal";

const FIELD = "w-full bg-transparent border border-aura-border rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:border-white/40 focus:ring-1 focus:ring-white/20 outline-none transition-colors duration-300";
const LABEL = "block text-xs uppercase tracking-[0.12em] text-zinc-500 mb-2";

const EMPTY = {
  first_name: "", last_name: "", email: "", password: "", country: "", address: "",
  instagram: "", tiktok: "", youtube: "",
  owns_quest3: "no", about: "",
};

export function ApplyForm() {
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { applySession } = useAuth();
  const { t, lang } = useLang();
  const ui = getLegalUI(lang);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.password || !form.country) {
      toast.error(t("apply.required"));
      return;
    }
    if (form.password.length < 6) {
      toast.error(t("apply.pwdMin"));
      return;
    }
    if (!consent) {
      toast.error(ui.consentRequired);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        owns_quest3: form.owns_quest3 === "yes",
      };
      const { data } = await api.post("/applications", payload);
      applySession(data);
      toast.success(t("apply.success"));
      navigate("/creator");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("apply.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="apply" data-testid="apply-section" className="relative z-10 bg-[#050505] py-24 md:py-36">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("apply.overline")}</span>
          <h2 className="font-display font-bold tracking-[-0.02em] text-white text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.02]">
            {t("apply.heading")}
          </h2>
          <p className="mt-5 text-zinc-400 text-lg">{t("apply.subtitle")}</p>
        </Reveal>

        <Reveal delay={0.05}>
          <form onSubmit={submit} data-testid="apply-form" className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div><label className={LABEL}>{t("apply.firstName")}</label><input data-testid="apply-first-name" className={FIELD} value={form.first_name} onChange={set("first_name")} placeholder="Marco" /></div>
              <div><label className={LABEL}>{t("apply.lastName")}</label><input data-testid="apply-last-name" className={FIELD} value={form.last_name} onChange={set("last_name")} placeholder="Rossi" /></div>
              <div><label className={LABEL}>{t("apply.email")}</label><input data-testid="apply-email" type="email" className={FIELD} value={form.email} onChange={set("email")} placeholder="tu@email.com" /></div>
              <div><label className={LABEL}>{t("apply.password")}</label><input data-testid="apply-password" type="password" className={FIELD} value={form.password} onChange={set("password")} placeholder={t("apply.passwordHint")} /></div>
              <div><label className={LABEL}>{t("apply.country")}</label><input data-testid="apply-country" className={FIELD} value={form.country} onChange={set("country")} placeholder="Italia" /></div>
              <div><label className={LABEL}>{t("apply.address")}</label><input data-testid="apply-address" className={FIELD} value={form.address} onChange={set("address")} /></div>
              <div><label className={LABEL}>{t("apply.instagram")}</label><input data-testid="apply-instagram" className={FIELD} value={form.instagram} onChange={set("instagram")} placeholder="@username" /></div>
              <div><label className={LABEL}>{t("apply.tiktok")}</label><input data-testid="apply-tiktok" className={FIELD} value={form.tiktok} onChange={set("tiktok")} placeholder="@username" /></div>
              <div><label className={LABEL}>{t("apply.youtube")}</label><input data-testid="apply-youtube" className={FIELD} value={form.youtube} onChange={set("youtube")} /></div>
              <div className="md:col-span-2">
                <label className={LABEL}>{t("apply.questQ")}</label>
                <div className="flex gap-3">
                  {[{ v: "yes", l: t("apply.yes") }, { v: "no", l: t("apply.no") }].map((o) => (
                    <button key={o.v} type="button" data-testid={`apply-quest-${o.v}`} onClick={() => setForm((f) => ({ ...f, owns_quest3: o.v }))}
                      className={`flex-1 rounded-lg border px-4 py-3 text-sm transition-colors duration-300 flex items-center justify-center gap-2 ${
                        form.owns_quest3 === o.v ? "border-white bg-white text-black font-semibold" : "border-aura-border text-zinc-300 hover:border-aura-borderhover"}`}>
                      {form.owns_quest3 === o.v && <Check size={16} />}{o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className={LABEL}>{t("apply.about")}</label>
                <textarea data-testid="apply-about" rows={4} className={FIELD} value={form.about} onChange={set("about")} placeholder={t("apply.aboutPlaceholder")} />
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 cursor-pointer">
              <input data-testid="apply-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-white shrink-0" />
              <span className="text-sm text-zinc-400 leading-relaxed">
                {ui.consentText}{" "}
                <Link to="/privacy" target="_blank" className="text-white underline underline-offset-2">{ui.consentPrivacy}</Link>{" "}
                {ui.consentAnd}{" "}
                <Link to="/termini" target="_blank" className="text-white underline underline-offset-2">{ui.consentTerms}</Link>.
              </span>
            </label>

            <button type="submit" disabled={loading} data-testid="apply-submit-btn"
              className="mt-6 w-full rounded-full bg-white text-black font-semibold text-base py-4 hover:bg-zinc-200 active:scale-[0.99] transition-[background-color,transform] duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <><Loader2 size={18} className="animate-spin" /> {t("apply.sending")}</> : t("apply.submit")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
